import React, { useState, useEffect } from 'react';
import api, { formatCurrency } from '../services/api';
import { BarChart3, Filter, TrendingUp, TrendingDown, Users, DollarSign } from 'lucide-react';
import { toast } from 'sonner';

export default function PartyAnalytics() {
  const [data, setData] = useState({ parties: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ party_type: '', date_from: '', date_to: '' });

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.party_type) params.party_type = filters.party_type;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      const res = await api.get('/reports/party-summary', { params });
      setData(res.data);
    } catch { toast.error('Failed to load party analytics'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const s = data.summary || {};
  const top10Sales = [...(data.parties || [])].sort((a, b) => b.total_sales - a.total_sales).slice(0, 10);
  const top10Purchase = [...(data.parties || [])].sort((a, b) => b.total_purchase - a.total_purchase).slice(0, 10);
  const maxSales = Math.max(...top10Sales.map(p => p.total_sales), 1);
  const maxPurchase = Math.max(...top10Purchase.map(p => p.total_purchase), 1);

  return (
    <div data-testid="party-analytics-page" className="space-y-6 max-w-7xl mx-auto">
      <div>
        <h1 className="font-heading font-bold text-2xl tracking-tight text-slate-900">Party Analytics</h1>
        <p className="text-sm text-slate-500 font-body">Party-wise sales, purchase, TDS & outstanding summary</p>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-md p-4 flex flex-wrap items-end gap-4" data-testid="analytics-filters">
        <div>
          <label className="form-label text-xs">Party Type</label>
          <select value={filters.party_type} onChange={e => setFilters(f => ({ ...f, party_type: e.target.value }))} className="form-input text-sm" data-testid="filter-party-type">
            <option value="">All Parties</option>
            <option value="customer">Customers Only</option>
            <option value="vendor">Vendors Only</option>
          </select>
        </div>
        <div><label className="form-label text-xs">From Date</label><input type="date" value={filters.date_from} onChange={e => setFilters(f => ({ ...f, date_from: e.target.value }))} className="form-input text-sm" /></div>
        <div><label className="form-label text-xs">To Date</label><input type="date" value={filters.date_to} onChange={e => setFilters(f => ({ ...f, date_to: e.target.value }))} className="form-input text-sm" /></div>
        <button onClick={load} className="btn-accent text-sm flex items-center gap-1.5" data-testid="apply-filters-btn"><Filter className="w-3.5 h-3.5" /> Apply</button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard label="Total Sales" value={s.total_sales || 0} icon={TrendingUp} color="emerald" />
        <SummaryCard label="Total Purchase" value={s.total_purchase || 0} icon={TrendingDown} color="amber" />
        <SummaryCard label="Outstanding" value={s.total_outstanding || 0} icon={DollarSign} color="rose" />
        <SummaryCard label="Total TDS" value={s.total_tds || 0} icon={Users} color="violet" />
      </div>

      {/* Charts: Top 10 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <ChartCard title="Top 10 Customers (by Sales)" data={top10Sales} valueKey="total_sales" max={maxSales} color="emerald" />
        <ChartCard title="Top 10 Vendors (by Purchase)" data={top10Purchase} valueKey="total_purchase" max={maxPurchase} color="amber" />
      </div>

      {/* Data Table */}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-slate-500" />
          <h3 className="font-heading font-semibold text-sm text-slate-800">Party-wise Breakdown ({data.parties?.length || 0} parties)</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="party-summary-table">
            <thead>
              <tr className="bg-slate-50 text-left">
                <th className="px-4 py-2.5 font-semibold text-slate-600 text-xs">Party Name</th>
                <th className="px-4 py-2.5 font-semibold text-slate-600 text-xs text-right">Total Sales</th>
                <th className="px-4 py-2.5 font-semibold text-slate-600 text-xs text-right">Total Purchase</th>
                <th className="px-4 py-2.5 font-semibold text-slate-600 text-xs text-right">Outstanding</th>
                <th className="px-4 py-2.5 font-semibold text-slate-600 text-xs text-right">TDS</th>
                <th className="px-4 py-2.5 font-semibold text-slate-600 text-xs text-right">Invoices</th>
                <th className="px-4 py-2.5 font-semibold text-slate-600 text-xs">Last Transaction</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="7" className="px-4 py-8 text-center text-slate-400">Loading...</td></tr>
              ) : (data.parties || []).length === 0 ? (
                <tr><td colSpan="7" className="px-4 py-8 text-center text-slate-400">No data found</td></tr>
              ) : (data.parties || []).map((p, i) => (
                <tr key={i} className="border-t border-slate-50 hover:bg-slate-50/50" data-testid={`party-row-${i}`}>
                  <td className="px-4 py-2.5 font-medium text-slate-800">{p.party_name}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-emerald-700">{formatCurrency(p.total_sales)}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-amber-700">{formatCurrency(p.total_purchase)}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-rose-700">{formatCurrency(p.total_outstanding)}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-violet-700">{formatCurrency(p.total_tds)}</td>
                  <td className="px-4 py-2.5 text-right text-slate-600">{p.invoice_count}</td>
                  <td className="px-4 py-2.5 text-slate-500">{p.last_transaction_date || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ label, value, icon: Icon, color }) {
  const colorMap = { emerald: 'bg-emerald-50 text-emerald-700', amber: 'bg-amber-50 text-amber-700', rose: 'bg-rose-50 text-rose-700', violet: 'bg-violet-50 text-violet-700' };
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4" data-testid={`summary-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center gap-2 mb-2"><div className={`p-1.5 rounded ${colorMap[color]}`}><Icon className="w-4 h-4" /></div><span className="text-xs text-slate-500 font-medium">{label}</span></div>
      <p className="font-mono font-bold text-xl text-slate-900">Rs. {formatCurrency(value)}</p>
    </div>
  );
}

function ChartCard({ title, data, valueKey, max, color }) {
  const barColor = color === 'emerald' ? 'bg-emerald-500' : 'bg-amber-500';
  return (
    <div className="bg-white border border-slate-200 rounded-md p-5">
      <h4 className="text-sm font-semibold text-slate-700 mb-4">{title}</h4>
      <div className="space-y-2">
        {data.map((p, i) => (
          <div key={i} className="flex items-center gap-3">
            <span className="text-xs text-slate-600 w-32 truncate">{p.party_name}</span>
            <div className="flex-1 bg-slate-100 rounded-full h-4 overflow-hidden">
              <div className={`h-full ${barColor} rounded-full transition-all`} style={{ width: `${Math.max((p[valueKey] / max) * 100, 2)}%` }} />
            </div>
            <span className="text-xs font-mono text-slate-600 w-20 text-right">{formatCurrency(p[valueKey])}</span>
          </div>
        ))}
        {data.length === 0 && <p className="text-xs text-slate-400 text-center py-4">No data</p>}
      </div>
    </div>
  );
}
